import * as paper from 'paper';
import { toLocalGuid } from 'src/shared/helpers';
import { Area } from '../areaDesigner.model';
import { AreaDesignerManager } from '../areaDesignerManager';
import { getItemChildrens } from '../helpers';
import AreaDesignerPlugin from './areaDesignerPlugin';

export class AreaDesignerLayersPlugin implements AreaDesignerPlugin {
    private layer!: paper.Layer;
    private manager!: AreaDesignerManager;

    public create = (manager: AreaDesignerManager) => {
        this.manager = manager;

        this.layer = new manager.paper.Layer();
        this.layer.bringToFront();
    };

    private createLayers = (item: paper.Item, area: Area) => {
        console.log('Creating layers...', item, area);
        for (const layer of area.layers) {
            const areaLayerItem = this.layer.importJSON(layer.json);
            areaLayerItem.data.areaGuid = area.guid;
            areaLayerItem.data.layerId = toLocalGuid(layer.id);
            areaLayerItem.strokeScaling = true;
        }

        this.updateLayers(item, area);
    };

    private updateLayers = (item: paper.Item, area: Area) => {
        if (area.templateFile && area.templateFile.documentWidth && area.templateFile.documentHeight && area.layers.length > 0) {
            const layers = Array.from(this.layer.children);

            let clipMaskLayer: paper.Item | null = null;

            // First get the clip mask layer
            for (const layer of area.layers) {
                if (layer.isClipMask) {
                    clipMaskLayer = layers.find((x) => x.data.layerId === toLocalGuid(layer.id))!;
                    break;
                }
            }

            // Get the offset from the clip mask layer or of the first layer which will give the bounds center
            let bounds: paper.Rectangle | null = null;
            let offsetCalculated = false;

            let offset!: paper.Point;
            let offsetFromDocumentCenter!: paper.Point;

            // Get the internal compound path because the layer contain an external document-sized shape
            if (clipMaskLayer) {
                const paths = getItemChildrens(clipMaskLayer).filter((x) => x instanceof this.manager.paper.CompoundPath && x.strokeColor);

                if (paths.length > 0) {
                    bounds = paths[0].bounds;

                    for (const path of paths) bounds = bounds.unite(path.bounds);
                }
            }

            for (const areaLayerItem of layers) {
                if (areaLayerItem.data.areaGuid === area.guid) {
                    // Scale the layers to the correct physical size
                    const scale = Math.min(
                        areaLayerItem.bounds.width / (area.templateFile.documentWidth * this.manager.ppcm),
                        areaLayerItem.bounds.height / (area.templateFile.documentHeight * this.manager.ppcm)
                    );

                    areaLayerItem.scale(1 / scale);

                    // Calculate layer offsets to move the layer and use in the output generation
                    if (!offsetCalculated) {
                        offsetCalculated = true;
                        offset = bounds ? bounds.center.subtract(item.position) : layers[0].position.subtract(item.position);
                        offsetFromDocumentCenter = bounds ? bounds.center.subtract(areaLayerItem.position) : new paper.Point(0, 0);
                    }

                    areaLayerItem.position = areaLayerItem.position.subtract(offset);
                    areaLayerItem.data.offsetFromDocumentCenter = [offsetFromDocumentCenter.x, offsetFromDocumentCenter.y];
                }
            }
        }
    };

    public onAreaItemCreated = (item: paper.Item, area: Area) => {
        this.createLayers(item, area);
    };

    public onAreaItemUpdated = (item: paper.Item, area: Area) => {
        this.updateLayers(item, area);
    };

    public onAreaMoving = (item: paper.Item, area: Area) => {
        this.updateLayers(item, area);
    };

    public onAreaResizing = (item: paper.Item, area: Area) => {
        this.updateLayers(item, area);
    };

    public onAreaItemRemoved = (item: paper.Item, areaGuid: string) => {
        for (const areaLayerItem of Array.from(this.layer.children)) {
            if (areaLayerItem.data.areaGuid === areaGuid) areaLayerItem.remove();
        }
    };

    public getUpdatedLayers = (area: Area) => {
        const layers = Array.from(this.layer.children);

        return area.layers.map((x) => ({
            ...x,
            json: layers.find((l) => l.data.layerId === toLocalGuid(x.id))!.exportJSON(),
            svg: layers.find((l) => l.data.layerId === toLocalGuid(x.id))!.exportSVG({ asString: true }) as string
        }));
    };
}
